import { adsEnabled } from '../../ads/config';
import { LEGAL } from './config';
import { Contact, LegalPage } from './LegalPage';

export function PrivacyPage() {
  const ads = adsEnabled();
  return (
    <LegalPage title="Privacy Policy">
      <p className="legal__lead">
        SuperFunUBG is run by {LEGAL.operator} (“we”). We built it to collect as little as possible. There are no accounts, and the only statistics we keep are anonymous, cookie-free visit counts.{' '}
        {ads ? (
          <>
            The site is free because it shows ads from Google; see <a href="#advertising">Advertising</a> below.
          </>
        ) : (
          'There are no ads and no tracking cookies.'
        )}
      </p>

      <h2>What we don’t collect</h2>
      <ul>
        <li>No names, email addresses, passwords or profiles. You never sign up.</li>
        <li>No tracking cookies, advertising profiles or social-media trackers from us.</li>
        <li>No chat or messaging between players.</li>
        {ads ? (
          <li>Our own fonts, images and game code are served from our site. Ads (Google) and our anonymous visit counter (Cloudflare) are the only content loaded from other companies.</li>
        ) : (
          <>
            <li>No advertising and no third-party cookies.</li>
            <li>Fonts, images and code are served from our own site. Our anonymous visit counter (Cloudflare) is the only content loaded from another company.</li>
          </>
        )}
      </ul>

      <h2>What is stored on your device</h2>
      <p>Your browser keeps some data so the site remembers it. This data stays on your device and is never sent to us:</p>
      <ul>
        <li>
          <strong>Settings:</strong> your sound on/off setting and your remapped keyboard controls.
        </li>
        <li>
          <strong>Game saves:</strong> best scores, unlocked levels and saved progress (for example a clicker game’s buildings or a sandbox world), plus any nickname or pet name you
          type into a game. Nicknames are only shown to you; the opponents in our games are computer-controlled bots.
        </li>
        <li>
          <strong>Favorites:</strong> the games you mark as favorites, so they appear at the top of the homepage.
        </li>
        <li>
          <strong>Likes:</strong> which games you gave a thumbs-up, so you can’t like the same game twice.
        </li>
      </ul>
      <p>You can delete all of it at any time by clearing this site’s data in your browser settings.</p>

      <h2>How the games run</h2>
      <p>Every game runs entirely inside your browser. There is no online multiplayer, chat or leaderboard, and your game progress and scores are never sent to us.</p>

      <h2>Anonymous visit statistics</h2>
      <p>
        To learn which games are popular and keep the site fast, we use Cloudflare Web Analytics. It does not use cookies or local storage, does not build a profile of you and does
        not follow you to other sites. For each page view it records things like the page address, the site that linked to it, the type of browser and device, the country (worked out
        from your IP address, which is not stored for this), and how fast the page loaded. We only see totals, never individual visitors. Cloudflare handles this data under its own
        privacy policy.
      </p>

      <p>
        We also keep our own simple visit counts, stored with our hosting provider, Cloudflare. For each visit they record which pages and games were opened, how long the site was on
        screen, the country, the general type of device (phone, tablet, Chromebook or computer) and the site that linked to us. There are no cookies and no IP addresses are kept. A
        random code that is forgotten when the browser tab closes joins up the pages of one visit, and the browser remembers only the date of its last visit so we can count visitors
        per day. None of this can tell us who you are, and it is never sold or shared.
      </p>

      <p>
        Game tiles show how many times each game has been played (added up from these visit counts) and how many likes it has. When you press a game’s thumbs-up button, the only thing
        sent to us is the game’s name and whether you liked or un-liked it. We keep a running total per game and nothing about who pressed it.
      </p>

      <p>
        Like any website, the company that hosts our site may automatically keep standard technical logs (such as IP addresses and request times) for security and reliability, under its
        own policies.
      </p>

      {ads && (
        <>
          <h2 id="advertising">Advertising</h2>
          <p>
            We show ads using Google AdSense. To deliver and measure ads and prevent ad fraud, Google and its partners may use cookies or similar technologies and receive
            information such as your IP address, browser and device type, and the page you’re viewing. This is handled by Google under{' '}
            <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noreferrer">
              Google’s policies for partner sites
            </a>
            .
          </p>
          <ul>
            <li>
              <strong>Non-personalized ads:</strong> because many players are young, we ask Google to show only non-personalized ads, which are based on things like the page and
              general location rather than your browsing history.
            </li>
            <li>
              <strong>Your consent:</strong> visitors in the European Economic Area, the UK and Switzerland are asked for consent through Google’s consent message before ads use
              cookies. You can change your choice at any time from the privacy link Google shows on the page.
            </li>
            <li>
              <strong>Your controls:</strong> learn about or turn off ad personalization in{' '}
              <a href="https://myadcenter.google.com/" target="_blank" rel="noreferrer">
                My Ad Center
              </a>
              , or block cookies in your browser. The games still work with ads blocked.
            </li>
          </ul>
          <p>Ads appear only around the games, never over the play area or during a round.</p>
        </>
      )}

      <h2>Game ideas and problem reports you send us</h2>
      <p>
        The “Suggest a Game” form on the homepage sends us only your short idea (up to 80 characters), with the time it was sent. The “Report a problem” button under each game
        sends only the game’s name and your short description of the problem. Neither asks for, and we do not want, a name, email or anything personal: messages that look like
        an email address, a link or a phone number are refused, and both forms remind players not to type their name or contact details. We use ideas to decide which games to
        make next and reports only to fix problems.
      </p>

      <h2>How we use information</h2>
      <p>
        Only to run the games, keep the service working, prevent abuse{ads ? ', and show ads as described above' : ''}. We don’t sell or rent information or build profiles of
        players.
      </p>

      <h2>Do Not Track</h2>
      <p>
        Some browsers can send a “Do Not Track” signal. We don’t track visitors across other websites, and our visit statistics are anonymous and cookie-free for everyone, so the
        site works the same whether or not this signal is on.{ads ? ' Ads are always requested as non-personalized.' : ''}
      </p>

      <h2>Children</h2>
      <p>
        Many of our players are children, so the whole site is built to collect as little as possible. There are no accounts, no chat, no way to talk to other players and
        no purchases. We don’t ask for, and do not knowingly collect, personal information from children under 13 (or from anyone): no names, email addresses, photos,
        locations or contact details. Game progress, favorites and likes stay in your own browser.{' '}
        {ads
          ? 'Every ad request is marked as directed to children, so ads are non-personalized and are not based on a child’s browsing history, in line with the Children’s Online Privacy Protection Act (COPPA). '
          : ''}
        If you are a parent or guardian and think your child has sent us personal information, for example by typing it into a form, email us and we’ll delete it.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>Clear this site’s data in your browser to remove saved settings.</li>
        <li>Contact us with any privacy question or request.</li>
      </ul>

      <h2>Changes</h2>
      <p>If this policy changes, we’ll update this page and its effective date. Significant changes will be noted on the site.</p>

      <h2>Contact</h2>
      <p>
        Questions about privacy: <Contact />
      </p>
    </LegalPage>
  );
}
